"use client";
import React, {
  ReactNode,
  useState,
  useEffect,
  useMemo,
} from "react";
import { User, Bell, X, Settings, HelpCircle, LogOut, LayoutDashboard } from "lucide-react";
import Image from "next/image";
import { motion, useReducedMotion } from "motion/react";
import Link from "next/link";
import { UserType } from "@/types/inde_menu";
import { formatDate } from "../utils";

// --- Theme Constants ---
export const THEME = {
  colors: {
    primary: "#2F4858",
    primaryLight: "#3A5A6E",
    primaryDark: "#243845",
    secondary: "#FF8C42",
    secondaryLight: "#FFA26B",
    secondaryDark: "#E67C3A",
    accent: "#FFE156",
    accentLight: "#FFF1A8",
    accentDark: "#EBCD4D",
    success: "#4CAF50",
    successLight: "#81C784",
    error: "#F44336",
    errorLight: "#FF7961",
    info: "#2196F3",
    infoLight: "#64B5F6",
    lightBg: "#F8F9FC",
    darkText: "#1E1E1E",
    mutedText: "#7B7B7B",
    cardBg: "#FFFFFF",
    white: "#FFFFFF",
    contentBg: "#FFFFFF",
    border: "#E5E7EB",
  },
  shadows: {
    sm: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
    md: "0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.03)",
    lg: "0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.02)",
    xl: "0 20px 25px rgba(0,0,0,0.04), 0 10px 10px rgba(0,0,0,0.02)",
    subtle: "0 2px 8px rgba(0,0,0,0.03)",
  },
  fonts: {
    heading: "'Playfair Display', serif",
    body: "'Inter', system-ui, sans-serif",
  },
  spacing: {
    content: {
      maxWidth: "1200px",
      sidePadding: "1.5rem",
      verticalSpacing: "2rem",
    },
  },
  transitions: {
    fast: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
    normal: "250ms cubic-bezier(0.4, 0, 0.2, 1)",
    slow: "350ms cubic-bezier(0.4, 0, 0.2, 1)",
    spring: "spring",
  },
};

export const ROLE_THEME = {
  customer: {
    accent: THEME.colors.info,
    badgeBg: THEME.colors.info,
    badgeText: THEME.colors.white,
    sidebarBg: "#F0F9FF",
    menuHover: "#E0F2FE",
    logoBorder: THEME.colors.info,
    hero: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2000&auto=format",
    gradientStart: "rgba(33,150,243,0.12)",
    gradientEnd: "rgba(33,150,243,0.03)",
  },
  waiter: {
    accent: THEME.colors.success,
    badgeBg: THEME.colors.success,
    badgeText: THEME.colors.white,
    sidebarBg: "#F0FDF4",
    menuHover: "#DCFCE7",
    logoBorder: THEME.colors.success,
    hero: "https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=2000&auto=format",
    gradientStart: "rgba(76,175,80,0.12)",
    gradientEnd: "rgba(76,175,80,0.03)",
  },
  vendor: {
    accent: THEME.colors.secondary,
    badgeBg: THEME.colors.secondary,
    badgeText: THEME.colors.white,
    sidebarBg: "#FFF7ED",
    menuHover: "#FFEDD5",
    logoBorder: THEME.colors.secondary,
    hero: "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?q=80&w=2000&auto=format",
    gradientStart: "rgba(255,140,66,0.12)",
    gradientEnd: "rgba(255,140,66,0.03)",
  },
  admin: {
    accent: "#8b5cf6",
    badgeBg: "#8b5cf6",
    badgeText: THEME.colors.white,
    sidebarBg: "#F5F3FF",
    menuHover: "#EDE9FE",
    logoBorder: "#8b5cf6",
    hero: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2000&auto=format",
    gradientStart: "rgba(139,92,246,0.12)",
    gradientEnd: "rgba(139,92,246,0.03)",
  },
} as const;

export type Role = keyof typeof ROLE_THEME;

export interface NavItem {
  label: string;
  icon: React.ReactNode;
  link: string;
  headerTitle?: string;
  targetRole?: Role | Role[];
  showOn?: Role[];
  always?: boolean;
}

export interface SidebarItem extends NavItem {}

export interface Notification {
  id: string;
  message: string;
  date: string;
  read: boolean;
  type?: "info" | "success" | "warning" | "error";
}

// --- Helper Functions ---
export function shouldShowForRole(item: NavItem, role: Role): boolean {
  if (item.always) return true;
  if (item.showOn) return item.showOn.includes(role);
  if (item.targetRole) {
    if (Array.isArray(item.targetRole)) return item.targetRole.includes(role);
    return item.targetRole === role;
  }
  return false;
}

// Helper function to convert HEX to RGBA
export function hexToRgba(hex: string, alpha: number): string {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// --- Backdrop for mobile overlays ---
export const Backdrop = ({
  show,
  onClick,
}: {
  show: boolean;
  onClick: () => void;
}) => (
  <>
    {show && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        onClick={onClick}
      />
    )}
  </>
);

// --- Loading Skeleton component ---
export const Skeleton = ({ className = "" }: { className?: string }) => (
  <div
    className={`animate-pulse rounded-md ${className}`}
    style={{
      background:
        "linear-gradient(110deg, #f0f0f0 30%, #fafafa 50%, #f0f0f0 70%)",
      backgroundSize: "200% 100%",
    }}
  />
);

// --- Empty State Component ---
export const EmptyState = ({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="flex flex-col items-center justify-center py-12 px-4 text-center"
  >
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 20 }}
      className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-50 mb-4 text-gray-400"
    >
      {icon}
    </motion.div>
    <motion.h3
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="text-lg font-medium text-gray-800 mb-2"
    >
      {title}
    </motion.h3>
    {description && (
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-sm text-gray-500 max-w-md mx-auto"
      >
        {description}
      </motion.p>
    )}
    {action && (
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={action.onClick}
        className="mt-4 px-4 py-2 text-sm font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
      >
        {action.label}
      </motion.button>
    )}
  </motion.div>
);

// --- Notification Card Component ---
export const NotificationCard = ({
  notification,
  accent,
  onClick,
}: {
  notification: Notification;
  accent: string;
  onClick?: (notification: Notification) => void;
}) => {
  // Map notification type to appropriate styling
  const typeStyles = {
    info: { icon: <Bell size={16} />, color: "#2196F3" },
    success: { icon: <Bell size={16} />, color: "#4CAF50" },
    warning: { icon: <Bell size={16} />, color: "#FF9800" },
    error: { icon: <Bell size={16} />, color: "#F44336" },
  };

  const style = notification.type
    ? typeStyles[notification.type]
    : { icon: <Bell size={16} />, color: accent };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{
        scale: 1.02,
        backgroundColor: "#F9FAFB",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      className={`
        flex items-start gap-3 p-3 rounded-lg cursor-pointer
        ${notification.read ? "bg-white" : "bg-blue-50/20"} 
        border border-transparent hover:border-gray-100
      `}
      onClick={() => onClick && onClick(notification)}
    >
      <motion.div
        whileHover={{ scale: 1.1, rotate: 5 }}
        className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center`}
        style={{ background: `${style.color}12`, color: style.color }}
      >
        {style.icon}
      </motion.div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm ${
            !notification.read
              ? "font-semibold text-gray-900"
              : "font-medium text-gray-700"
          }`}
        >
          {notification.message}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {formatDate(notification.date)}
        </p>
      </div>
      {!notification.read && (
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ repeat: Infinity, repeatType: "reverse", duration: 2 }}
          className="w-2 h-2 rounded-full mt-2"
          style={{ backgroundColor: style.color }}
        ></motion.div>
      )}
    </motion.div>
  );
};

// --- Notification Drawer ---
export const NotificationDrawer = ({
  open,
  onClose,
  notifications = [],
  accent,
  onNotificationClick,
}: {
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  accent: string;
  onNotificationClick?: (notification: Notification) => void;
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <>
      {open && (
        <motion.div
          initial={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, x: 20, scale: 0.95 }
          }
          animate={
            prefersReducedMotion
              ? { opacity: 1 }
              : {
                  opacity: 1,
                  x: 0,
                  scale: 1,
                }
          }
          exit={
            prefersReducedMotion
              ? { opacity: 0 }
              : {
                  opacity: 0,
                  x: 20,
                  scale: 0.95,
                }
          }
          transition={
            !prefersReducedMotion
              ? {
                  type: "spring",
                  stiffness: 400,
                  damping: 30,
                }
              : { duration: 0.2 }
          }
          className="fixed top-16 right-4 z-50 w-full sm:w-96 bg-white border shadow-xl rounded-xl overflow-hidden"
          style={{
            boxShadow: THEME.shadows.xl,
            maxHeight: "calc(100vh - 5rem)",
          }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div className="font-semibold text-lg flex items-center gap-2">
              <motion.div whileHover={{ rotate: [0, -10, 10, -10, 0] }}>
                <Bell size={17} style={{ color: accent }} />
              </motion.div>
              <span style={{ color: THEME.colors.darkText }}>
                Notifications
              </span>
            </div>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200 transition"
              aria-label="Close notification"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <X size={18} />
            </motion.button>
          </div>
          <div className="p-2 max-h-[calc(100vh-12rem)] overflow-y-auto">
            <>
              {notifications.length === 0 ? (
                <EmptyState
                  icon={<Bell size={24} />}
                  title="No notifications"
                  description="You're all caught up! We'll notify you when something new arrives."
                />
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col gap-1"
                >
                  {notifications.map((notification, index) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <NotificationCard
                        notification={notification}
                        accent={accent}
                        onClick={(n) => {
                          onNotificationClick && onNotificationClick(n);
                          onClose();
                        }}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </>
          </div>
          {notifications.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center p-3 border-t"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-sm font-medium hover:underline transition"
                style={{ color: accent }}
              >
                Mark all as read
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      )}
    </>
  );
};

// --- User Menu Dropdown ---
export const UserMenuDropdown = ({
  open,
  onClose,
  onLogout,
  onProfileClick,
  user,
  accent,
}: {
  open: boolean;
  onClose: () => void;
  onLogout?: () => void;
  onProfileClick?: () => void;
  user: UserType;
  accent: string;
}) => {
  // Menu items
  const menuItems = [
    {
      label: "Profile",
      icon: <User size={16} />,
      onClick: () => {
        onProfileClick && onProfileClick();
        onClose();
      },
    },
    {
      label: "Settings",
      icon: <Settings size={16} />,
      onClick: onClose,
    },
    {
      label: "Help & Support",
      icon: <HelpCircle size={16} />,
      onClick: onClose,
    },
  ];

  const prefersReducedMotion = useReducedMotion();

  return (
    <>
      {open && (
        <motion.div
          initial={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, y: -10, scale: 0.95 }
          }
          animate={
            prefersReducedMotion
              ? { opacity: 1 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          exit={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, y: -10, scale: 0.95 }
          }
          transition={
            !prefersReducedMotion
              ? {
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                }
              : { duration: 0.2 }
          }
          className="absolute right-0 top-12 z-50 w-64 bg-white border shadow-xl rounded-xl overflow-hidden"
          style={{ boxShadow: THEME.shadows.xl }}
        >
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 p-4 border-b"
          >
            <div className="flex-shrink-0 relative">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
                className="w-12 h-12 rounded-full overflow-hidden border-2 shadow-sm"
                style={{ borderColor: accent }}
              >
                {user?.avatar ? (
                  <Image
                    src={user.avatar}
                    alt={user.name || "User"}
                    width={48}
                    height={48}
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <User size={24} className="text-gray-500" />
                  </div>
                )}
              </motion.div>
              <motion.div
                animate={{
                  scale: [1, 1.15, 1],
                  borderWidth: ["2px", "1px", "2px"],
                }}
                transition={{
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 3,
                  repeatDelay: 1,
                }}
                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white"
              />
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {user?.name || "User"}
              </div>
              {user?.email && (
                <div className="text-xs text-gray-500">{user.email}</div>
              )}
            </div>
          </motion.div>

          <div className="py-1">
            {menuItems.map((item, idx) => (
              <motion.button
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + idx * 0.05 }}
                whileHover={{
                  backgroundColor: hexToRgba(accent, 0.05),
                  x: 3,
                }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={item.onClick}
              >
                <span className="mr-3 text-gray-500">{item.icon}</span>
                {item.label}
              </motion.button>
            ))}
          </div>

          <div className="border-t py-1">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              whileHover={{ backgroundColor: "#FEE2E2" }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              onClick={() => {
                onLogout && onLogout();
                onClose();
              }}
            >
              <LogOut className="mr-3" size={16} />
              Sign out
            </motion.button>
          </div>
        </motion.div>
      )}
    </>
  );
};
 
// --- Content Container Component ---
export const ContentContainer = ({
  children,
  className = "",
  noPadding = false,
}: {
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`
      bg-white rounded-xl shadow-sm overflow-hidden
      ${noPadding ? "" : "p-5 sm:p-6"}
      ${className}
    `}
    style={{ boxShadow: THEME.shadows.subtle }}
    whileHover={{ boxShadow: THEME.shadows.md }}
  >
    {children}
  </motion.div>
);

// --- Section Title Component ---
export const SectionTitle = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 mb-6"
  >
    <div>
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-xl font-semibold text-gray-900"
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mt-1 text-sm text-gray-500"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
    {action && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {action}
      </motion.div>
    )}
  </motion.div>
);

// --- Page Breadcrumb ---
export const Breadcrumb = ({
  items,
}: {
  items: { label: string; href?: string }[];
}) => (
  <motion.nav
    initial={{ opacity: 0, y: -5 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    aria-label="Breadcrumb"
    className="mb-5"
  >
    <ol className="flex items-center space-x-1 text-sm">
      <motion.li initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }}>
        <Link
          href="/dashboard"
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
          >
            <LayoutDashboard size={16} />
          </motion.div>
          <span className="sr-only">Dashboard</span>
        </Link>
      </motion.li>

      {items.map((item, i) => (
        <motion.li
          key={i}
          className="flex items-center"
          initial={{ opacity: 0, x: -5 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
        >
          <span className="mx-1 text-gray-400">/</span>
          {item.href ? (
            <Link
              href={item.href}
              className="text-gray-500 hover:text-gray-700 hover:underline transition-colors"
            >
              {item.label}
            </Link>
          ) : (
            <span className="font-medium text-gray-900">{item.label}</span>
          )}
        </motion.li>
      ))}
    </ol>
  </motion.nav>
);

// --- Hero Banner Component ---
 
type HeroBannerProps = {
  roleTheme: (typeof ROLE_THEME)[Role];
  targetRole: Role;
};

export const HeroBanner = ({ roleTheme, targetRole }: HeroBannerProps) => {
  const roleLabel = useMemo(
    () => targetRole.charAt(0).toUpperCase() + targetRole.slice(1),
    [targetRole]
  );

  const formattedDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const description = useMemo(() => {
    switch (targetRole) {
      case "customer":
        return "Manage your orders and reservations efficiently from one central location.";
      case "waiter":
        return "Stay on top of tables and customers with ease.";
      case "vendor":
        return "Control menu items and orders seamlessly.";
      default:
        return "Oversee restaurant operations effortlessly.";
    }
  }, [targetRole]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
      whileHover={{ y: -3, boxShadow: THEME.shadows.md }}
      className="relative w-full h-[170px] sm:h-[180px] rounded-xl overflow-hidden mb-6 shadow-sm"
    >
      {/* Background image */}
      {roleTheme.hero && (
        <Image
          src={roleTheme.hero}
          alt={`${roleLabel} dashboard banner`}
          fill
          className="object-cover"
          priority
          sizes="(max-width:640px) 100vw, (max-width:1024px) 80vw, 1200px"
        />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.1,
            type: "spring",
            stiffness: 100,
            damping: 20,
          }}
          className="max-w-3xl"
        >
          {/* Badge + Date */}
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <motion.span
              whileHover={{ scale: 1.05 }}
              className="px-3 py-1 text-xs font-semibold rounded-full"
              style={{
                background: roleTheme.badgeBg,
                color: roleTheme.badgeText,
              }}
            >
              <h2 className="text-xs font-semibold">{roleLabel} Dashboard</h2>
            </motion.span>

            <div className="flex items-center gap-2 text-xs font-medium text-white/90">
              <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
              {formattedDate}
            </div>
          </div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-sm text-white/80 max-w-xl"
          >
            {description}
          </motion.p>
        </motion.div>
      </div>
    </motion.section>
  );
};


// --- Current Time Component ---
export const CurrentTime = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  return (
    <p className="text-xs text-gray-500">
      {currentTime.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}
    </p>
  );
};

